/*
 * -----------------------------------------------------------------------
 *  <copyright file="products-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';
import { useGridCells, useTranslation } from 'hooks';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import ExpandableGrid from 'views/components/core/expandable/grid';

import './styles.scss';

const ProductsGrid = ({ products, actions }: any) => {
    const { t } = useTranslation();
    const { MaxLengthCell, DateCell } = useGridCells();
    const [productsData, setProductsData] = useState<[]>([]);

    useEffect(() => {
        if (products) {
            const productsArray = [...products].map((product: any) => ({
                ...product,
                referenceNumber: product.referenceNumber || '',
                activeIngredients: product.activeIngredients || '',
                registrationNumber: product.registrationNumber || '',
            }));
            const newData = restructuredUpdatedCreatedBy(productsArray);
            setProductsData(orderBy(newData, [{ field: 'name', dir: 'asc' }]));
        }
    }, [products]);

    return (
        <ExpandableGrid
            customKey="products-grid"
            id="productsGrid"
            key="productsGrid"
            items={productsData}
            rowActions={actions}
            groupable={false}
            showPager
            addScroll
            scrollable="scrollable"
            reorderable
            pageable={false}
            resizable
            options={{
                allowExcelExport: false,
                allowExpandCollapseAll: false,
                useFilterChips: false,
            }}
            detail={null}
            columns={[
                {
                    field: 'name',
                    label: t('admin:product'),
                    id: 'product-name',
                    minWidth: 140,
                    cells: { data: MaxLengthCell(70) },
                    lockedByDefault: true,
                },
                {
                    field: 'referenceNumber',
                    id: 'product-referenceNumber',
                    label: t('admin:referenceNumber'),
                    minWidth: 210,
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    field: 'registrationNumber',
                    id: 'product-registrationNumber',
                    label: t('admin:registrationNumber'),
                    minWidth: 230,
                    cells: { data: MaxLengthCell(70) },
                },
                {
                    field: 'activeIngredients',
                    id: 'product-activeIngredients',
                    label: t('admin:activeIngredients'),
                    minWidth: 210,
                    cells: { data: MaxLengthCell(150) },
                },
                {
                    label: t('ra:createdDate'),
                    id: 'product-created',
                    minWidth: 200,
                    field: 'created',
                    showByDefault: true,
                    cells: { data: DateCell },
                    filter: 'date',
                },
                {
                    label: t('ra:createdBy'),
                    id: 'product-createdby',
                    field: '_createdByString',
                    showByDefault: true,
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'product-updated',
                    minWidth: 200,
                    filter: 'date',
                    showByDefault: true,
                    cells: { data: DateCell },
                },
                {
                    label: t('ra:updatedBy'),
                    id: 'product-updatedby',
                    field: '_updatedByString',
                    showByDefault: true,
                    minWidth: 180,
                },
                {
                    field: 'recordId',
                    label: t('id'),
                    id: 'product-id',
                    filter: 'numeric',
                    minWidth: 140,
                    showByDefault: false,
                },
            ]}
        />
    );
};

export default ProductsGrid;
